import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  BULK_PHRASE_CONFIRM_THRESHOLD,
  formatBulkIdPreview,
  formatBulkImpactCount,
  getConfirmationLevelLabel,
  getConfirmationRequirements,
} from "./bulkActionSafety";

function BulkImpactBanner({
  count,
  impactVerb = "impactadas",
  irreversible = true,
  level = 0,
  entitySingular = "solicitação",
  entityPlural = "solicitações",
}) {
  const severity = level >= 3 ? "error" : level >= 2 ? "warning" : "info";

  return (
    <Alert
      severity={severity}
      icon={<WarningAmberIcon fontSize="inherit" />}
      sx={{
        "& .MuiAlert-message": { width: "100%" },
        alignItems: "flex-start",
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="h5" component="p" fontWeight={700} lineHeight={1.2}>
          {formatBulkImpactCount(count, impactVerb, {
            singular: entitySingular,
            plural: entityPlural,
          })}
        </Typography>

        {irreversible && (
          <Typography variant="body2" fontWeight={600}>
            Esta operação é irreversível — não há desfazer em lote.
          </Typography>
        )}

        {level > 0 && (
          <Chip
            size="small"
            label={`Confirmação ${getConfirmationLevelLabel(level).toLowerCase()} (nível ${level})`}
            color={level >= 3 ? "error" : level >= 2 ? "warning" : "default"}
            sx={{ alignSelf: "flex-start", fontWeight: 600 }}
          />
        )}
      </Stack>
    </Alert>
  );
}

/**
 * Etapa de revisão antes de executar ação em massa.
 * @param {{ label: string, value: string, severity?: 'warning'|'error' }[]} changeLines
 */
export default function BulkActionConfirmStep({
  ids = [],
  actionTitle = "Confirmar operação em massa",
  actionDescription,
  impactVerb = "impactadas",
  /** Total impactado (se omitido, usa ids.length). */
  impactCount,
  entitySingular = "solicitação",
  entityPlural = "solicitações",
  impactedIdsLabel,
  impactedIdsFallback,
  typedCountHelperText,
  criticalImpactMessage,
  changeLines = [],
  warnings = [],
  confirmCheckboxLabel,
  reviewCheckboxLabel,
  irreversible = true,
  forceTypedConfirmation = false,
  minConfirmationLevel = 0,
  onBack,
  onConfirm,
  submitting = false,
  confirmButtonLabel = "Confirmar e aplicar",
  confirmButtonColor = "primary",
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);
  const [typedCount, setTypedCount] = useState("");
  const [typedPhrase, setTypedPhrase] = useState("");

  const count =
    impactCount != null && Number.isFinite(Number(impactCount))
      ? Number(impactCount)
      : ids.length;
  const showIdList = ids.length > 0;

  const idsSignature = useMemo(
    () => (Array.isArray(ids) ? ids.join(",") : ""),
    [ids]
  );
  const changeLinesSignature = useMemo(
    () => JSON.stringify(changeLines),
    [changeLines]
  );

  const requirements = useMemo(
    () =>
      getConfirmationRequirements(count, {
        forceTypedConfirmation,
        minLevel: minConfirmationLevel,
      }),
    [count, forceTypedConfirmation, minConfirmationLevel]
  );

  const idsCaption =
    impactedIdsLabel ||
    (entitySingular === "solicitação"
      ? `IDs das solicitações selecionadas (${count})`
      : `IDs dos ${entityPlural} selecionados (${count})`);
  const defaultTypedHelper =
    entitySingular === "solicitação"
      ? `Confirmação nível ${requirements.level}: digite o número exato de solicitações impactadas.`
      : `Confirmação nível ${requirements.level}: digite o número exato de ${entityPlural} impactados.`;

  const typedCountOk =
    !requirements.requireTypedCount || String(typedCount).trim() === String(count);
  const phraseOk =
    !requirements.requirePhrase ||
    typedPhrase.trim().toUpperCase() === requirements.confirmPhrase;
  const reviewOk = !requirements.requireReviewAck || reviewAcknowledged;

  const canConfirm = acknowledged && reviewOk && typedCountOk && phraseOk && !submitting;

  useEffect(() => {
    setAcknowledged(false);
    setReviewAcknowledged(false);
    setTypedCount("");
    setTypedPhrase("");
  }, [idsSignature, changeLinesSignature, count, requirements.level]);

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {actionTitle}
          </Typography>

          <BulkImpactBanner
            count={count}
            impactVerb={impactVerb}
            irreversible={irreversible}
            level={requirements.level}
            entitySingular={entitySingular}
            entityPlural={entityPlural}
          />

          {actionDescription && (
            <Typography variant="body2" color="text.secondary">
              {actionDescription}
            </Typography>
          )}

          {requirements.isCriticalImpact && (
            <Alert severity="error" variant="outlined">
              {criticalImpactMessage ||
                `Operação de impacto crítico: ${count} ${entityPlural} (a partir de ${BULK_PHRASE_CONFIRM_THRESHOLD}). Revise cada detalhe antes de prosseguir.`}
            </Alert>
          )}

          {warnings.map((msg, i) => (
            <Alert key={i} severity="warning">
              {msg}
            </Alert>
          ))}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
              border: 1,
              borderColor: "divider",
            }}
          >
            {showIdList ? (
              <>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {idsCaption}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-word" }}>
                  {formatBulkIdPreview(ids)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {impactedIdsFallback ||
                  `${count} ${entityPlural} serão incluídos nesta operação.`}
              </Typography>
            )}
          </Box>

          {changeLines.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Alterações que serão aplicadas
              </Typography>
              <List dense disablePadding sx={{ bgcolor: "action.hover", borderRadius: 1, px: 1 }}>
                {changeLines.map((line) => (
                  <ListItem key={line.label} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          color={
                            line.severity === "error"
                              ? "error.main"
                              : line.severity === "warning"
                                ? "warning.dark"
                                : "text.primary"
                          }
                        >
                          <strong>{line.label}:</strong> {line.value}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Box sx={{ pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Confirmações necessárias
            </Typography>

            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    disabled={submitting}
                  />
                }
                label={
                  confirmCheckboxLabel ||
                  `Entendo que ${formatBulkImpactCount(count, impactVerb)} e que esta operação é irreversível.`
                }
              />

              {requirements.requireReviewAck && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reviewAcknowledged}
                      onChange={(e) => setReviewAcknowledged(e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label={
                    reviewCheckboxLabel ||
                    `Revisei a lista de IDs acima e confirmo que são exatamente os ${entityPlural} corretos.`
                  }
                />
              )}

              {requirements.requireTypedCount && (
                <TextField
                  fullWidth
                  size="small"
                  label={`Digite ${count} para confirmar a quantidade`}
                  value={typedCount}
                  onChange={(e) => setTypedCount(e.target.value.replace(/\D/g, ""))}
                  disabled={submitting}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  helperText={typedCountHelperText || defaultTypedHelper}
                  error={typedCount.length > 0 && !typedCountOk}
                />
              )}

              {requirements.requirePhrase && (
                <TextField
                  fullWidth
                  size="small"
                  label={`Digite ${requirements.confirmPhrase} para autorizar`}
                  value={typedPhrase}
                  onChange={(e) => setTypedPhrase(e.target.value)}
                  disabled={submitting}
                  helperText={`Confirmação nível ${requirements.level}: última barreira antes de aplicar em ${count} ${entityPlural}.`}
                  error={typedPhrase.length > 0 && !phraseOk}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack} disabled={submitting}>
          Voltar
        </Button>
        <Button
          variant="contained"
          color={confirmButtonColor}
          onClick={onConfirm}
          disabled={!canConfirm}
        >
          {confirmButtonLabel}
        </Button>
      </DialogActions>
    </>
  );
}
